import React from 'react'
import { getBrowserFromUserAgent } from '@sketch/utils'

interface UseDragProps {
  type: 'share' | 'project'
  identifier: string
  customDragImage?: boolean
  customDragImageStyles?: React.CSSProperties
}

function composeHandlers<E>(
  ...fns: Array<((event: E) => void) | undefined>
): (event: E) => void {
  return (event: E) => {
    for (const fn of fns) {
      fn?.(event)
    }
  }
}

function applyStyles(styles: React.CSSProperties, element: HTMLElement): void {
  for (const [property, value] of Object.entries(styles)) {
    if (value) {
      // Transform camelCase properties to kebab-case for CSS
      const cssProperty = property.replace(
        /[A-Z]/g,
        match => `-${match.toLowerCase()}`
      )
      element.style.setProperty(cssProperty, value.toString())
    }
  }
}

/**
 * This function exposes handlers to set a custom drag image for an element.
 *
 * Drag and Drop API already allow to set a custom drag image but with several
 * limitations (when using `event.dataTransfer.setDragImage` with a cloned
 * element, the browser generates a ghost image, but you can't tweak
 * almost any visual aspect of it).
 *
 * So, the only option at this point is to create a "real" custom drag element,
 * hiding the default native ghost image. This gives complete freedom to style
 * the drag image, but it adds complexity and is error-prone.
 */
const customDragImageHandlers = (
  styles: UseDragProps['customDragImageStyles']
) => {
  const onCustomDragStart = (event: React.DragEvent<HTMLElement>) => {
    const element = event.target as HTMLElement

    // Hide browser native drag image using a custom one
    const hiddenDragImage = element.cloneNode(true) as HTMLElement
    hiddenDragImage.id = 'hidden-drag-image'
    hiddenDragImage.style.opacity = '0'
    document.body.appendChild(hiddenDragImage)

    event.dataTransfer.setDragImage(hiddenDragImage, 0, 0)

    // Set custom drag image
    const customDragImage = element.cloneNode(true) as HTMLElement
    customDragImage.id = 'custom-drag-image'
    customDragImage.style.position = 'absolute'
    customDragImage.style.zIndex = '99999'
    customDragImage.style.pointerEvents = 'none'
    customDragImage.style.opacity = '0'
    customDragImage.style.width = `${element.clientWidth}px`

    if (styles) {
      applyStyles(styles, customDragImage)
    }

    // Let's make sure we remove any existing custom drag image before
    // appending a new one
    document.getElementById('custom-drag-image')?.remove()

    document.body.appendChild(customDragImage)
  }

  const onCustomDragEnd = (event: React.DragEvent<HTMLElement>) => {
    const hiddenDragImage = document.getElementById('hidden-drag-image')
    const customDragImage = document.getElementById('custom-drag-image')

    hiddenDragImage?.remove()
    customDragImage?.remove()
  }

  const onCustomDrag = (event: React.DragEvent<HTMLElement>) => {
    const customDragImage = document.getElementById('custom-drag-image')

    if (customDragImage) {
      // In some browsers the event.clientX and event.clientY returns 0 before
      // the drag starts, we want to avoid this situation
      if (event.clientX > 0 && event.clientY > 0) {
        // By default we set the opacity to 0 so it doesn't show up, but we
        // want to show the custom drag image when the drag event starts
        if (customDragImage.style.opacity === '0') {
          customDragImage.style.opacity = styles?.opacity
            ? `${styles?.opacity}`
            : '0.8'
        }
        const x = event.clientX - customDragImage.clientWidth / 2
        const y = event.clientY - customDragImage.clientHeight / 2
        const translation = `translate(${x}px, ${y}px)`

        customDragImage.style.transform = styles?.transform
          ? `${translation} ${styles?.transform}`
          : translation
      }
    }
  }

  return {
    onCustomDragStart,
    onCustomDragEnd,
    onCustomDrag,
  }
}

/**
 * Hook that exposes attributes to drag an element, accepts dragging both
 * shares or projects (includes the shareId or projectId in the event.dataTransfer data)
 */
export const useDrag = ({
  type,
  identifier,
  customDragImage,
  customDragImageStyles,
}: UseDragProps) => {
  const { onCustomDragStart, onCustomDragEnd, onCustomDrag } =
    customDragImageHandlers(customDragImageStyles)

  const onDragStart = (event: React.DragEvent<HTMLElement>) => {
    event.stopPropagation()

    /** Set data-being-dragged to true so we know which element started the drag */
    /** This value is later reset in onDragEnd (down below) */
    const element = event.target as HTMLElement

    const stringifiedParameters =
      type === 'project'
        ? JSON.stringify({ projectId: identifier })
        : JSON.stringify({ shareId: identifier })

    event.dataTransfer.setData('text/plain', stringifiedParameters)
    event.dataTransfer.effectAllowed = 'move'

    // When dragging over an element, dataTransfer.getData('text/plain') is not
    // available, so we store the dragged element info in a data attribute
    element.dataset.beingDragged = stringifiedParameters

    // Set a custom drag image if one is specified
    const dragImage = element.querySelector('[data-drag-image]')
    if (dragImage) {
      event.dataTransfer.setDragImage(dragImage, 0, 0)
    }
  }

  const onDragEnd = (event: React.DragEvent<HTMLElement>) => {
    /** Reset the data-being-dragged attribute  */
    const element = event.target as HTMLElement
    delete element.dataset.beingDragged
  }

  /**
   * Firefox has an old bug regarding drag events and screen coordinates,
   * so we can't use custom drag images with this browser.
   * See: https://bugzilla.mozilla.org/show_bug.cgi?id=505521
   */
  const applyCustomDragImage =
    getBrowserFromUserAgent(navigator.userAgent || '') !== 'Firefox' &&
    customDragImage

  return {
    onDragStart: applyCustomDragImage
      ? composeHandlers(onDragStart, onCustomDragStart)
      : onDragStart,
    onDragEnd: applyCustomDragImage
      ? composeHandlers(onDragEnd, onCustomDragEnd)
      : onDragEnd,
    onDrag: applyCustomDragImage ? onCustomDrag : undefined,
  }
}
